<template>
  <v-card class="fill-height d-flex admin-page" flat tile>
    <LMap
      ref="adminMap"
      :min-zoom="13"
      :max-zoom="16"
      :zoom="14"
      :center="center"
      :options="{ zoomControl: false, scrollWheelZoom: true }"
      class="fill-height"
    >
      <LTileLayer :url="baseTileUrl" />
      <div v-for="m in manholes" :key="m.id">
        <LMarker :lat-lng="[m.lat, m.lng]" @click="selectMarker(m)">
          <LIcon :options="{ html: m.iconHtml }" :icon-anchor="[14, 14]" />
          <LTooltip :options="tooltipOption">
            {{ m.name }}
          </LTooltip>
        </LMarker>
      </div>
    </LMap>
    <v-card tile flat class="manhole-container" min-width="480">
      <v-card tile flat class="px-2 d-flex align-center" height="45">
        <v-btn small class="ml-auto" color="secondary" @click="save()">
          保存
        </v-btn>
      </v-card>
      <v-simple-table dense fixed-header height="calc(100dvh - 50px)">
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left"></th>
              <th class="text-left">名称</th>
              <th class="text-left">一般</th>
              <th class="text-left">職員</th>
              <th class="text-left">ﾊﾞｯﾃﾘ(V)</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="m in manholes"
              :key="m.id"
              :id="`row-${m.id}`"
              @click="selectList(m)"
            >
              <td :class="getSelectedClass(m)">
                <div v-html="m.iconHtml" />
              </td>
              <td>
                {{ m.name }}
              </td>
              <td>
                <v-select
                  dense
                  v-model="m.publicStatus"
                  :items="statusItems"
                  item-text="text"
                  item-value="value"
                  class="selector my-0"
                  hide-details
                  :background-color="getItemColor(m.publicStatus)"
                />
              </td>
              <td>
                <v-select
                  dense
                  v-model="m.privateStatus"
                  :items="statusItems"
                  item-text="text"
                  item-value="value"
                  class="selector my-0"
                  hide-details
                  :background-color="getItemColor(m.privateStatus)"
                />
              </td>
              <td>
                {{ m.batt }}
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-card>
    <v-snackbar v-model="snackbar">
      {{ snackbarText }}
    </v-snackbar>
  </v-card>
</template>

<script>
import { LMap, LTileLayer, LMarker, LTooltip, LIcon } from "vue2-leaflet";
import L from "leaflet";
import { BRANCH_OFFICE } from "../enums/BranchOffice";
import { OBSERVATORY } from "../enums/Type";
import * as axiosHelper from "../utils/axiosHelper";
import * as cognito from "../utils/cognito";
import * as s3 from "../utils/s3";

export default {
  name: "Admin",

  components: {
    LMap,
    LTileLayer,
    LMarker,
    LTooltip,
    LIcon
  },

  data() {
    return {
      baseTileUrl: "https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png",
      center: new L.LatLng(BRANCH_OFFICE[0].lat, BRANCH_OFFICE[0].lng),
      manholes: [],
      snackbar: false,
      snackbarText: "",
      manholeIconHtml: `<div class="v-icon notranslate mdi mdi-alpha-m-circle theme--light" style="color: #24B9AA; font-size: 28px;" />`,
      waterwayIconHtml: `<div class="v-icon notranslate mdi mdi-bitbucket theme--light" style="color: #24B9AA; font-size: 28px;" />`,
      selectedId: "",
      map: null,
      tooltipOption: {
        direction: "top",
        offset: [0, -14]
      },
      tooltip: null,
      statusItems: [
        { text: "正常配信", value: "normal" },
        { text: "メンテ中", value: "maintenance" },
        { text: "非表示", value: "invisible" }
      ]
    };
  },

  computed: {
    isAdmin() {
      return this.$store.state.user.profile === "admin";
    }
  },

  async mounted() {
    let session = null;
    try {
      session = await cognito.isAuthenticated();
    } catch (e) {
      session = await cognito.isAuthenticated();
    }
    if (!session) {
      this.$router.replace("/login");
      return;
    }

    if (!this.isAdmin) {
      this.$router.replace("/");
      return;
    }
    this.map = this.$refs.adminMap.mapObject;
    await this.fetchManholes();
    this.tooltip = L.tooltip(this.tooltipOption);
  },

  methods: {
    getStatusClass(status) {
      return { "font-weight-bold": this.status === status };
    },

    async fetchManholes() {
      // front config
      const frontConfig = await s3.getFrontConfig();
      if (frontConfig === null) {
        this.manholes = [];
        return;
      }
      const statuses = frontConfig.statuses;

      // observatories
      const res = await axiosHelper.get("/observatories", {
        domain: process.env.VUE_APP_DOMAIN
      });
      if (res === null || !res.observatories) {
        this.manholes = [];
        return;
      }

      // manhole status
      let manholeValues = {};
      const statusRes = await axiosHelper.get("/observatories/status/v2", {
        domain: process.env.VUE_APP_DOMAIN
      });
      if (statusRes !== null && statusRes.manholes) {
        manholeValues = statusRes.manholes;
      }

      const manholes = res.observatories.filter(
        obs =>
          obs.type === OBSERVATORY.MANHOLE.code ||
          obs.type === OBSERVATORY.MANHOLE_WATERWAY.code
      );
      manholes.forEach(m => {
        if (m.type === OBSERVATORY.MANHOLE.code) {
          m.iconHtml = this.manholeIconHtml;
        } else {
          m.iconHtml = this.waterwayIconHtml;
        }
        if (statuses[m.id]) {
          m.publicStatus = statuses[m.id].public;
          m.privateStatus = statuses[m.id].private;
        } else {
          m.publicStatus = "normal";
          m.privateStatus = "normal";
        }
        if (manholeValues[m.id]) {
          m.batt = manholeValues[m.id].batt.toFixed(1);
        } else {
          m.batt = "---";
        }
      });
      this.manholes = manholes;
      this.map.invalidateSize();
    },

    getItemColor(status) {
      switch (status) {
        case "normal":
          return "transparent";
        case "maintenance":
          return "#BBDEFB";
        case "invisible":
          return "#BDBDBD";
      }
    },

    displayTooltip(target) {
      this.tooltip.remove();
      this.tooltip.setLatLng([target.lat, target.lng]);
      this.tooltip.setContent(target.name);
      this.tooltip.addTo(this.map);
    },

    selectList(target) {
      this.selectedId = target.id;
      if (!this.map) return;
      if (target.lat && target.lng) {
        this.map.setView([target.lat, target.lng], 15);
        this.displayTooltip(target);
      }
    },

    selectMarker(target) {
      this.selectedId = target.id;
      this.displayTooltip(target);

      // リストを見える位置にスクロール
      const targetIndex = this.manholes.findIndex(m => m.id === target.id);
      if (targetIndex < 0) return;
      const id = `#row-${target.id}`;
      const height = this.$el.querySelector(id).clientHeight;
      const tableWrapper = this.$el.getElementsByClassName(
        "v-data-table__wrapper"
      )[0];
      tableWrapper.scroll({ top: height * targetIndex, behavior: "smooth" });
    },

    getSelectedClass(row) {
      return row.id === this.selectedId ? "selected" : "unselected";
    },

    async save() {
      const statuses = {};
      this.manholes.forEach(m => {
        statuses[m.id] = {
          public: m.publicStatus,
          private: m.privateStatus
        };
      });
      const data = {
        statuses: statuses
      };
      const res = await s3.putFrontConfig(data);
      if (res.statusCode === 200) {
        this.snackbarText = "保存しました";
        this.snackbar = true;
      }
    }
  }
};
</script>
<style lang="scss" scoped>
.admin-page {
  width: calc(100vw - 5px);
  .manhole-container {
    border-left: 1px solid rgb(202, 202, 202) !important;
  }
  th {
    padding: 2px 5px !important;
  }
  td {
    font-size: 12px !important;
    padding: 2px 5px 2px 8px !important;
    white-space: pre-wrap !important;
    .v-input--selection-controls {
      margin: 0 !important;
    }
    .v-input--selection-controls__ripple {
      margin: 0 !important;
    }
  }
  td:first-child {
    width: 45px;
    padding: 0;
  }
  .selected {
    border-left: 4px solid rgb(255, 121, 121);
  }
  .unselected {
    border-left: 4px solid white;
  }
  .selector {
    width: 100px;
    .v-text-field > .v-input__control > .v-input__slot:before {
      border: none !important;
    }
    .v-text-field > .v-input__control > .v-input__slot:before {
      border-style: none !important;
    }
  }
}
</style>

<style>
.selector .v-select__selections {
  font-size: 14px !important;
}
.selector .v-input__slot:before {
  border: none !important;
}
</style>
